import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/api.service';
import { formatDate } from '../../utils/dateFormat';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';

// Same toolbar as the typing-center Quotations page: one 32px row.
const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

// The customer sees SENT as "Awaiting you" — it is waiting for their accept/reject.
const STATUS_CONFIG = {
  DRAFT: { color: 'warning', label: 'Draft' },
  FINAL: { color: 'primary', label: 'Final' },
  SENT: { color: 'info', label: 'Awaiting you' },
  ACCEPTED: { color: 'success', label: 'Accepted' },
  REJECTED: { color: 'error', label: 'Rejected' },
  EXPIRED: { color: 'default', label: 'Expired' },
  CONVERTED: { color: 'secondary', label: 'Converted' }
};

const StatusChip = ({ status }) => {
  const config = STATUS_CONFIG[status] || { color: 'default', label: status };
  return <Chip label={config.label} color={config.color} size="small" />;
};

const formatCurrency = (amount) => new Intl.NumberFormat('en-AE', {
  style: 'currency',
  currency: 'AED'
}).format(parseFloat(amount) || 0);

const quotationNumber = (q) => q.quotation_number || q.quotationNumber || q.draft_number || q.draftNumber || '—';

const PortalQuotations = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchQuotations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getPortalQuotations();
      setQuotations(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch quotations:', err);
      setError('Failed to load quotations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
  }, []);

  const counts = useMemo(() => {
    const by = (s) => quotations.filter((q) => q.status === s).length;
    return { total: quotations.length, SENT: by('SENT'), ACCEPTED: by('ACCEPTED'), REJECTED: by('REJECTED') };
  }, [quotations]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return quotations.filter((q) => {
      if (statusFilter && q.status !== statusFilter) return false;
      if (!term) return true;
      return quotationNumber(q).toLowerCase().includes(term);
    });
  }, [quotations, searchTerm, statusFilter]);

  useEffect(() => { setPage(0); }, [searchTerm, statusFilter]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const open = (q) => navigate(`/portal/quotations/${q.id}`);

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button size="small" onClick={fetchQuotations} sx={{ textTransform: 'none' }}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      <ProTableStats
        loading={loading}
        cards={[
          { key: '', label: 'Total', value: counts.total },
          { key: 'SENT', label: 'Awaiting you', value: counts.SENT, color: 'info.main' },
          { key: 'ACCEPTED', label: 'Accepted', value: counts.ACCEPTED, color: 'success.main' },
          { key: 'REJECTED', label: 'Rejected', value: counts.REJECTED, color: 'error.main' },
        ].map((c) => ({
          ...c,
          highlight: statusFilter === c.key,
          onClick: () => setStatusFilter(c.key),
        }))}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
        <TextField
          size="small"
          placeholder="Search by quotation number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: '1 1 200px', minWidth: 200, maxWidth: 420 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
            <MenuItem value="SENT" sx={{ fontSize: '0.8125rem' }}>Awaiting you</MenuItem>
            <MenuItem value="ACCEPTED" sx={{ fontSize: '0.8125rem' }}>Accepted</MenuItem>
            <MenuItem value="REJECTED" sx={{ fontSize: '0.8125rem' }}>Rejected</MenuItem>
            <MenuItem value="CONVERTED" sx={{ fontSize: '0.8125rem' }}>Converted</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {filtered.length} quotations
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchQuotations} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>Quotation #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Valid Until</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {quotations.length === 0 ? 'No quotations yet' : 'No matching quotations'}
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((q) => (
                  <TableRow key={q.id} hover sx={{ cursor: 'pointer' }} onClick={() => open(q)}>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{quotationNumber(q)}</TableCell>
                    <TableCell>{formatDate(q.quotation_date || q.quotationDate, '—')}</TableCell>
                    <TableCell>{formatDate(q.valid_until || q.validUntil, '—')}</TableCell>
                    <TableCell align="right">{formatCurrency(q.subtotal)}</TableCell>
                    <TableCell align="right">{formatCurrency(q.tax_total ?? q.taxTotal)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(q.grand_total ?? q.grandTotal)}</TableCell>
                    <TableCell align="center"><StatusChip status={q.status} /></TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Quotation">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); open(q); }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default PortalQuotations;
